import { Modal } from '@components/core/modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminCreateUse } from '@queries/admin-uses.query';
import { useSchema } from '@schemas/use.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { Loader2Icon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { adminUseFormOptions } from './admin-use-form-options';

type AdminUseCreateModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AdminUseCreateModal: React.FC<AdminUseCreateModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const [, setSelectedUseId] = useQueryState('useId', parseAsString);
  const { mutateAsync: createUse, isPending } = useAdminCreateUse();

  const form = useAppForm({
    ...adminUseFormOptions,
    validators: {
      onSubmit: useSchema,
    },
    onSubmit: async ({ value }) => {
      await createUse(
        {
          description: value.description || undefined,
          name: value.name ?? '',
        },
        {
          onSuccess: async (data) => {
            successToast({ description: 'Use created successfully' });
            await setSelectedUseId(data.id);
            void handleClose();
          },
          onError: (error) => errorToast({ description: error.message || 'Failed to create use' }),
        },
      );
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={handleClose}
      header={{
        className: 'text-left',
        title: 'Create Use',
      }}
      footer={{
        rightButton: (
          <div className="flex gap-2 justify-end">
            <Button
              disabled={isPending}
              onClick={handleClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={() => form.handleSubmit()}
              type="button"
            >
              {isPending && <Loader2Icon className="size-3 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        ),
      }}
      width="640px"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-4">
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  autoComplete="off"
                  data-1p-ignore
                  label="Name"
                  placeholder="Enter a use name"
                />
              )}
            </form.AppField>

            <form.AppField name="description">
              {(field) => (
                <field.TextAreaField
                  label="Description"
                  placeholder="Enter a use description"
                  rows={3}
                />
              )}
            </form.AppField>
          </div>
        </form.AppForm>
      </form>
    </Modal>
  );
};
